import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';

const RATINGS = [
  { value: 'E', label: 'Everyone', desc: 'Suitable for all ages', color: '#10B981' },
  { value: 'E10', label: 'Everyone 10+', desc: 'Mild content', color: '#F59E0B' },
  { value: 'T', label: 'Teen', desc: 'Ages 13 and up', color: '#6366F1' },
];

export default function PublishModal({ game, onClose, onPublish }) {
  const [title, setTitle] = useState(game?.title || '');
  const [description, setDescription] = useState(game?.description || '');
  const [rating, setRating] = useState(game?.rating || 'E');
  const [copied, setCopied] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const shareLink = `${window.location.origin}/game-player-screen?id=${game?.id}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const el = document.createElement('textarea');
      el.value = shareLink;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handlePublish = async () => {
    setIsPublishing(true);
    await new Promise(r => setTimeout(r, 600));
    onPublish({ ...game, title: title?.trim() || game?.title, description, rating, published: !game?.published });
    setIsPublishing(false);
    onClose();
  };

  const handleShareSocial = (platform) => {
    const text = encodeURIComponent(`Check out my game "${title}" on GameForge!`);
    const url = encodeURIComponent(shareLink);
    const urls = {
      Twitter: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      Discord: `https://discord.com/`,
      Reddit: `https://reddit.com/submit?url=${url}&title=${text}`,
    };
    if (urls[platform]) window.open(urls[platform], '_blank', 'noopener,noreferrer');
  };

  if (!game) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}
      onClick={(e) => e?.target === e?.currentTarget && onClose()}
    >
      <div
        className="w-full max-w-lg rounded-2xl overflow-hidden animate-bounce-in"
        style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-xl)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: 'var(--color-border)' }}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(16,185,129,0.1)' }}>
              <Icon name="Globe" size={16} color="var(--color-success)" />
            </div>
            <h2 className="text-base font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
              {game?.published ? 'Update Published Game' : 'Publish Game'}
            </h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-[var(--color-muted)] transition-all">
            <Icon name="X" size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
          <Input
            label="Game Title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e?.target?.value)}
            placeholder="Enter a catchy title..."
            required
          />

          <div>
            <label className="block text-sm font-semibold mb-1.5" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e?.target?.value)}
              placeholder="Describe your game for other players..."
              rows={3}
              className="w-full resize-none rounded-xl border p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] transition-all"
              style={{ borderColor: 'var(--color-border)', background: 'var(--color-card)', color: 'var(--color-foreground)', fontFamily: 'var(--font-body)' }}
            />
          </div>

          {/* Content rating */}
          <div>
            <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Content Rating</p>
            <div className="flex gap-2">
              {RATINGS?.map((r) => (
                <button
                  key={r?.value}
                  onClick={() => setRating(r?.value)}
                  className="flex-1 flex flex-col items-center gap-1 p-2.5 rounded-xl border text-xs transition-all"
                  style={{
                    borderColor: rating === r?.value ? r?.color : 'var(--color-border)',
                    background: rating === r?.value ? `${r?.color}14` : 'var(--color-muted)',
                    color: rating === r?.value ? r?.color : 'var(--color-muted-foreground)',
                  }}
                >
                  <span className="font-bold text-sm" style={{ fontFamily: 'var(--font-heading)' }}>{r?.value}</span>
                  <span className="font-semibold" style={{ fontFamily: 'var(--font-caption)' }}>{r?.label}</span>
                  <span style={{ fontFamily: 'var(--font-caption)' }}>{r?.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Share link */}
          <div>
            <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Share Link</p>
            <div className="flex gap-2">
              <div
                className="flex-1 px-3 py-2 rounded-xl text-xs overflow-hidden text-ellipsis whitespace-nowrap"
                style={{ background: 'var(--color-muted)', color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-data)', border: '1px solid var(--color-border)' }}
              >
                {shareLink}
              </div>
              <button
                onClick={handleCopy}
                className="px-3 py-2 rounded-xl text-xs font-semibold flex items-center gap-1.5 transition-all"
                style={{
                  background: copied ? 'rgba(16,185,129,0.1)' : 'rgba(99,102,241,0.1)',
                  color: copied ? 'var(--color-success)' : 'var(--color-primary)',
                  border: `1px solid ${copied ? 'var(--color-success)' : 'var(--color-primary)'}`,
                }}
              >
                <Icon name={copied ? 'Check' : 'Copy'} size={13} />
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          {/* Social share */}
          <div className="flex gap-2">
            {[
              { label: 'Twitter', icon: 'Twitter', color: '#1DA1F2' },
              { label: 'Discord', icon: 'MessageCircle', color: '#5865F2' },
              { label: 'Reddit', icon: 'Share2', color: '#FF4500' },
            ]?.map((s) => (
              <button
                key={s?.label}
                onClick={() => handleShareSocial(s?.label)}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-80"
                style={{ background: `${s?.color}18`, color: s?.color, border: `1px solid ${s?.color}40`, fontFamily: 'var(--font-caption)' }}
              >
                <Icon name={s?.icon} size={13} color={s?.color} />
                {s?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-5 border-t" style={{ borderColor: 'var(--color-border)' }}>
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button
            variant={game?.published ? 'outline' : 'success'}
            onClick={handlePublish}
            iconName={game?.published ? 'EyeOff' : 'Globe'}
            iconPosition="left"
            className="flex-1"
            disabled={!title?.trim()}
            loading={isPublishing}
          >
            {game?.published ? 'Unpublish' : 'Publish Game'}
          </Button>
        </div>
      </div>
    </div>
  );
}